import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import Tooltip from '../../components/molecules/Tooltip';
import PermissionsWrapper from '../../containers/PermissionsWrapper';
import GoalProgressBar from './GoalProgressBar';
import { formatAmount, getGoalStatus, isValidPercent, trackGoalsEvent } from '../utils';
import { GOAL_STATUSES, TOTAL_BUDGET_ENTRY_LABEL, TRANSLATION_KEY } from '../constants';
import css from '../style.css';

const STATUS_CHIP_CLASSES = {
    [GOAL_STATUSES.ACTIVE]: 'statusChipActive',
    [GOAL_STATUSES.UPCOMING]: 'statusChipUpcoming',
    [GOAL_STATUSES.ENDED]: 'statusChipEnded',
};

const GoalCard = ({ goal, currencySymbol = '', onEdit, onDuplicate, onDelete, translate = () => {} }) => {
    const [expanded, setExpanded] = useState(false);
    const hasMetricData = isValidPercent(goal.actual_metric_percentage);
    const hasSubGoals = (goal.entries || []).some(entry => entry.name !== TOTAL_BUDGET_ENTRY_LABEL);
    const status = getGoalStatus(goal);

    const toggleExpand = () => {
        if (!hasMetricData) {
            return;
        }
        trackGoalsEvent(expanded ? 'Goal Entries Collapsed' : 'Goal Entries Expanded', { goal_id: goal.public_id });
        setExpanded(!expanded);
    };

    return (
        <div className={css.goalCard}>
            <PermissionsWrapper write>
                <div className={css.goalCardActions}>
                    <Tooltip titleTranslationKey={`${TRANSLATION_KEY}.DUPLICATE_GOAL`}>
                        <button
                            type="button"
                            className={css.goalCardAction}
                            aria-label={translate(`${TRANSLATION_KEY}.DUPLICATE_GOAL`)}
                            onClick={() => onDuplicate(goal)}
                        >
                            <Icon name="copy" size={IconSizes.SMALL} />
                        </button>
                    </Tooltip>
                    <Tooltip titleTranslationKey={`${TRANSLATION_KEY}.EDIT_GOAL`}>
                        <button
                            type="button"
                            className={css.goalCardAction}
                            aria-label={translate(`${TRANSLATION_KEY}.EDIT_GOAL`)}
                            onClick={() => onEdit(goal)}
                        >
                            <Icon name="pencil" size={IconSizes.SMALL} />
                        </button>
                    </Tooltip>
                    <Tooltip titleTranslationKey={`${TRANSLATION_KEY}.DELETE_GOAL`}>
                        <button
                            type="button"
                            className={classNames(css.goalCardAction, css.goalCardActionDelete)}
                            aria-label={translate(`${TRANSLATION_KEY}.DELETE_GOAL`)}
                            onClick={() => onDelete(goal)}
                        >
                            <Icon name="trash" size={IconSizes.SMALL} />
                        </button>
                    </Tooltip>
                </div>
            </PermissionsWrapper>

            <div className={css.goalCardTitle}>
                <span className={css.goalMetricName}>{goal.metricName}</span>
                <span className={css.goalName}>{goal.name}</span>
                <span className={css.goalDates}>
                    {goal.start_date} - {goal.end_date}
                </span>
                <span className={classNames(css.statusChip, css[STATUS_CHIP_CLASSES[status]])}>
                    <Translate id={`${TRANSLATION_KEY}.STATUS_${status.toUpperCase()}`} />
                </span>
            </div>

            <div className={css.goalMetricsRow}>
                <div className={css.goalAmounts}>
                    <div className={css.goalMetricLine}>
                        <span className={css.goalMetricLabel}>
                            <Translate id={`${TRANSLATION_KEY}.GOAL_LABEL`} />
                        </span>
                        <span className={css.goalMetricValue}>
                            {currencySymbol} {formatAmount(goal.metric_sum)}
                        </span>
                    </div>
                    <div className={css.goalMetricLine}>
                        <span className={css.goalMetricLabel}>
                            <Translate id={`${TRANSLATION_KEY}.ACTUAL_LABEL`} />
                        </span>
                        <span className={css.goalMetricValue}>
                            {hasMetricData ? `${currencySymbol} ${formatAmount(goal.current_metric)}` : '—'}
                        </span>
                    </div>
                </div>
                <div className={css.goalProgressColumn}>
                    <div className={css.goalMetricLine}>
                        <span className={css.goalProgressLabel}>
                            <Translate id={`${TRANSLATION_KEY}.PERIOD_COMPLETED`} />
                        </span>
                        <GoalProgressBar value={goal.period_complete} />
                        <span className={css.goalRemainingDays}>{goal.remaining_days}</span>
                    </div>
                    <div className={css.goalMetricLine}>
                        <span className={css.goalProgressLabel}>
                            <Translate id={`${TRANSLATION_KEY}.GOAL_COMPLETED`} />
                        </span>
                        <GoalProgressBar value={goal.actual_metric_percentage} warning />
                    </div>
                </div>
            </div>

            {hasSubGoals && (
                <Tooltip titleTranslationKey={`${TRANSLATION_KEY}.NO_DATA_TOOLTIP`} disabled={hasMetricData}>
                    <button
                        type="button"
                        className={hasMetricData ? css.subGoalsToggle : css.subGoalsToggleDisabled}
                        onClick={toggleExpand}
                    >
                        <Icon
                            name={expanded ? 'chevron-up' : 'chevron-down'}
                            size={IconSizes.X_SMALL}
                            className={css.subGoalsToggleIcon}
                        />
                        <Translate id={`${TRANSLATION_KEY}.${expanded ? 'HIDE_SUB_GOALS' : 'SHOW_SUB_GOALS'}`} />
                    </button>
                </Tooltip>
            )}

            {hasSubGoals && expanded && (
                <div className={css.subGoalsList}>
                    {goal.entries.map(entry => (
                        <div key={entry.id} className={css.subGoal}>
                            <div className={css.subGoalName}>{entry.name}</div>
                            <div className={css.goalMetricsRow}>
                                <div className={css.goalAmounts}>
                                    <div className={css.goalMetricLine}>
                                        <span className={css.goalMetricLabel}>
                                            <Translate id={`${TRANSLATION_KEY}.TARGET_COST`} />
                                        </span>
                                        <span className={css.goalMetricValue}>
                                            {currencySymbol} {formatAmount(entry.metric_goal)}
                                        </span>
                                    </div>
                                    <div className={css.goalMetricLine}>
                                        <span className={css.goalMetricLabel}>
                                            <Translate id={`${TRANSLATION_KEY}.ACTUAL_COST`} />
                                        </span>
                                        <span className={css.goalMetricValue}>
                                            {currencySymbol} {formatAmount(entry.current_metric)}
                                        </span>
                                    </div>
                                </div>
                                <div className={css.goalProgressColumn}>
                                    <div className={css.goalMetricLine}>
                                        <span className={css.goalProgressLabel}>
                                            <Translate id={`${TRANSLATION_KEY}.ACTUAL_VS_PLANNED`} />
                                        </span>
                                        <GoalProgressBar value={entry.actual_metric_percentage} warning />
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

GoalCard.propTypes = {
    goal: PropTypes.shape({
        public_id: PropTypes.string,
        name: PropTypes.string,
        metricName: PropTypes.string,
        start_date: PropTypes.string,
        end_date: PropTypes.string,
        metric_sum: PropTypes.number,
        current_metric: PropTypes.number,
        actual_metric_percentage: PropTypes.string,
        period_complete: PropTypes.string,
        remaining_days: PropTypes.string,
        entries: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string,
                metric_goal: PropTypes.number,
                current_metric: PropTypes.number,
                actual_metric_percentage: PropTypes.string,
            })
        ),
    }).isRequired,
    currencySymbol: PropTypes.string,
    onEdit: PropTypes.func.isRequired,
    onDuplicate: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    translate: PropTypes.func,
};

export default GoalCard;
